// ===========================================
// State that belongs to ONE browser history entry
// ===========================================
// `useState` that survives leaving the page and coming BACK to it (the browser's Back
// button), and only that. It is keyed by React Router's `location.key`, which the browser
// restores with the history entry, so:
//   - Back returns to the same entry → the same key → the saved value comes back;
//   - opening the page afresh (a link, the menu) is a NEW entry → a new key → nothing.
//
// Built for "An invoice may need amending" (mid-term start week, /qa Phase 3 D-c, Andrew
// 2026-10-01): the admin follows one invoice's Amend link, amends it, presses Back, and the
// list is still there for the next family — in one tab, never new ones.
//
// Outside a router (a component rendered on its own, as in many tests) there is no history
// entry: it is plain `useState`. The location is read from the context directly because
// `useLocation()` THROWS outside a router.
//
// sessionStorage: per tab, gone when the tab closes. Every access is wrapped — storage can
// be unavailable (private mode, blocked site data) and the page must work without it.

import { useCallback, useContext, useEffect, useState } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';

const PREFIX = 'mnm:history-entry:';

/**
 * Forget every stored entry — called on LOGOUT, so a different user in the same tab cannot
 * press Back into the previous user's list (family names, invoice numbers). Never throws.
 */
export function clearHistoryEntryState(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable: nothing was stored.
  }
}

function read<T>(storageKey: string): T | undefined {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    return raw === null ? undefined : (JSON.parse(raw) as T);
  } catch {
    return undefined;
  }
}

/**
 * @param name   what is stored, unique per page (e.g. 'lessons-amend-prompt')
 * @param empty  the "nothing to keep" value (null, []): it is never stored
 */
export function useHistoryEntryState<T>(
  name: string,
  empty: T
): [T, (next: T | ((prev: T) => T)) => void] {
  const key = useContext(UNSAFE_LocationContext)?.location?.key;
  // 'default' is the key of the entry a tab first LOADS on, and every fresh load of a page
  // gets it again — so it does not identify one visit. Keeping state under it would bring
  // an old list back on a later load of the same page; such an entry keeps plain state.
  const storageKey = key && key !== 'default' ? `${PREFIX}${name}:${key}` : null;
  const [value, setValue] = useState<T>(() => (storageKey ? read<T>(storageKey) : undefined) ?? empty);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const isEmpty = value === empty || (Array.isArray(value) && value.length === 0);
      if (isEmpty) window.sessionStorage.removeItem(storageKey);
      else window.sessionStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // Storage unavailable: the value still works for this visit, Back just won't restore it.
    }
    // `empty` is a constant per call site; only the value and the entry matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, storageKey]);

  const set = useCallback((next: T | ((prev: T) => T)) => setValue(next), []);
  return [value, set];
}
